import { defineMessages } from 'react-intl-next';

import { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { expValEquals, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';

import type { GeneralAccessLevel } from '../controlled/commonTypes.tsx';
import type { GeneralAccessProps } from '../controlled/shareAndRestrictDialog/header/generalAccess/GeneralAccess.tsx';
import type { GeneralAccessLevelSelectorProps } from '../controlled/shareAndRestrictDialog/header/generalAccess/GeneralAccessLevelSelector.tsx';
import type { GeneralAccessPermissionDropdownProps } from '../controlled/shareAndRestrictDialog/header/generalAccess/GeneralAccessPermissionDropdown.tsx';
import type { HeaderProps } from '../controlled/shareAndRestrictDialog/header/Header.tsx';
import type { InfoMessageProps } from '../controlled/shareAndRestrictDialog/header/infoMessage/InfoMessage.tsx';
import type { PublishModalInfoBoxProps } from '../controlled/shareAndRestrictDialog/header/infoMessage/PublishModalInfoBox.tsx';
import type { OverflowMenuProps } from '../controlled/shareAndRestrictDialog/header/OverflowMenu.tsx';
import type { PrincipalsStagerProps } from '../controlled/shareAndRestrictDialog/header/PrincipalsStager.tsx';
import type { TitleAndButtonsProps } from '../controlled/shareAndRestrictDialog/header/TitleAndButtons.tsx';
import type { ContentType } from '../shareAndRestrictButton/ShareRestrictAndCopyCoordinator.tsx';
import type { SpecificAccessFacePileRowProps } from '../controlled/shareAndRestrictDialog/header/specificAccess/SpecificAccessFacePileRow.tsx';
import type { FacepileRowProps } from '../controlled/shareAndRestrictDialog/header/specificAccess/facepileRow/FacepileRow.tsx';
import type { SpecificAccessFilterProps } from '../controlled/shareAndRestrictDialog/header/SpecificAccessFilter.tsx';

import { type StatesAndActions } from './Controller.tsx';
import { ConfluenceGroupUsageType } from './queries/__types__/useQueriesShareAndRestrictDialogQuery.ts';
import { makeSpecificAccessDetailsProps } from './makeBodyProps.ts';
import { makeCtaNotifyMessageFooterProps } from './makeFooterProps.ts';

export function makeHeaderProps(statesAndActions: StatesAndActions): HeaderProps {
	const {
		masterState: {
			isPublicLinkDialogOpen,
			isShareToSlackDialogOpen,
			isAccessRequestsDialogOpen,
			isSpecificAccessDialogOpen,
			tabSelection,
		},
		derivedState: { isChanged, specificAccessIds, shouldShowDeletedGroupDetails, natureOfCta },
		actions: { setTabSelection },
	} = statesAndActions;

	const isAnotherDialogOpen =
		isPublicLinkDialogOpen ||
		isShareToSlackDialogOpen ||
		isAccessRequestsDialogOpen ||
		isSpecificAccessDialogOpen;

	const hideSpecificAccessDetails = () => {
		if (shouldShowDeletedGroupDetails) {
			return false;
		}

		return (
			isAnotherDialogOpen ||
			specificAccessIds.length === 0 ||
			(natureOfCta !== 'none' &&
				expValEquals('persistence_specific_access_gate', 'cohort', 'experiment'))
		);
	};

	const isSpecificAccessTabSelected = tabSelection === 0;

	return {
		titleAndButtons: makeTitleAndButtonsProps(statesAndActions),
		generalAccess:
			(natureOfCta !== 'none' &&
				expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')) ||
			isAnotherDialogOpen
				? undefined
				: makeGeneralAccessProps(statesAndActions),
		principalsStager: isAnotherDialogOpen ? undefined : makePrincipalsStagerProps(statesAndActions),
		specificAccessTabs:
			isSpecificAccessDialogOpen &&
			expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
				? {
						tabSelection,
						setTabSelection,
					}
				: undefined,
		specificAccessFilter:
			isSpecificAccessDialogOpen &&
			isSpecificAccessTabSelected &&
			expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
				? makeSpecificAccessFilterProps(statesAndActions)
				: undefined,
		infoMessage: makeInfoMessageProps(statesAndActions),
		hasNavigationLockout: isChanged,
		specificAccessFacePileRow: hideSpecificAccessDetails()
			? undefined
			: makeSpecificAccessFacePileRowProps(statesAndActions),
		ctaNotifyMessageFooter: expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
			? makeCtaNotifyMessageFooterProps(statesAndActions)
			: undefined,
	};
}

function makeInfoMessageProps(statesAndActions: StatesAndActions): InfoMessageProps | undefined {
	const {
		controllerProps: { contentType, contentId },
		queriesState: { edition },
		derivedState: {
			isRestrictionsEditable,
			isPublishModalFlow,
			willBeUnpublishedAfterApplying,
			stagedGeneralAccessLevel,
		},
		masterState: { isPublicLinkDialogOpen },
	} = statesAndActions;

	const messageType = getMessageType(
		contentType,
		isRestrictionsEditable,
		isPublishModalFlow,
		willBeUnpublishedAfterApplying,
		edition,
		stagedGeneralAccessLevel,
		isPublicLinkDialogOpen,
		canShowAskToEditInfoMessage(statesAndActions),
	);

	return messageType === undefined
		? undefined
		: {
				messageType,
				publishModalInfoBoxProps: isPublishModalFlow
					? makePublishModalInfoBoxProps(statesAndActions)
					: undefined,
				askToEditBoxProps:
					messageType === 'askToEdit' ? { contentId, stagedGeneralAccessLevel } : undefined,
			};
}

export function canShowAskToEditInfoMessage(statesAndActions: StatesAndActions): boolean {
	const {
		queriesState: { userCanEditContent },
	} = statesAndActions;

	if (userCanEditContent) {
		return false;
	}

	const { principals } = makeSpecificAccessDetailsProps(statesAndActions);

	const currentUserHasSpecificAccessToEdit = principals
		.filter((p) => p !== null)
		.some(
			(principal) =>
				principal.principalDetails.isYou && principal.principalAccess.access === 'can-edit',
		);

	if (currentUserHasSpecificAccessToEdit) {
		// We don't show Ask to Edit Info Message if user has specific access to EDIT
		// BTW they are most likely blocked at space / parent level, because
		// at this point we know for a fact they cannot edit the document
		return false;
	}

	return true;
}

function getMessageType(
	contentType: ContentType,
	isRestrictionsEditable: boolean,
	isPublishModalFlow: boolean,
	willBeUnpublishedAfterApplying: boolean,
	edition: ConfluenceEdition | null,
	generalAccessLevel: GeneralAccessLevel,
	isPublicLinkDialogOpen: boolean,
	isReadyToShowAskToEditInfoMessage: boolean,
): InfoMessageProps['messageType'] {
	if (edition === 'FREE') {
		// Free edition upsell is only shown for open-can-edit
		if (isPublicLinkDialogOpen) return 'freeEditionPublicLinkUpsell';
		return generalAccessLevel === 'open-can-edit' ? 'freeEditionUpsell' : undefined;
	}

	if (contentType === 'embed') {
		return 'smartLink';
	}

	if (
		!isRestrictionsEditable &&
		!UNSAFE_expValEqualsNoExposure('confluence_contextual_ask_to_edit', 'cohort', 'experiment')
	) {
		return 'uneditableRestrictions';
	}

	if (isPublishModalFlow) {
		return 'publishModal';
	}

	if (willBeUnpublishedAfterApplying && generalAccessLevel !== 'restricted') {
		return 'draft';
	}

	if (
		isReadyToShowAskToEditInfoMessage &&
		UNSAFE_expValEqualsNoExposure('confluence_contextual_ask_to_edit', 'cohort', 'experiment')
	) {
		return 'askToEdit';
	}

	return undefined;
}

function makePublishModalInfoBoxProps(
	statesAndActions: StatesAndActions,
): PublishModalInfoBoxProps {
	const {
		controllerProps: { contentType },
		derivedState: { isUnpublishedDraft },
	} = statesAndActions;
	return {
		isUnpublishedDraft,
		contentType,
	};
}

function makeTitleAndButtonsProps(statesAndActions: StatesAndActions): TitleAndButtonsProps {
	const {
		actions: {
			dismiss,
			closePublicLinkDialog,
			closeShareToSlackDialog,
			closeAccessRequestsDialog,
			openAccessRequestsDialog,
			clearElementToFocus,
			closeSpecificAccessDialog,
		},
		masterState: {
			isPublicLinkDialogOpen,
			isShareToSlackDialogOpen,
			isAccessRequestsDialogOpen,
			lastTriggeredDialogVariant,
			isSpecificAccessDialogOpen,
		},
	} = statesAndActions;

	let title: TitleAndButtonsProps['title'],
		onBackClick: TitleAndButtonsProps['onBackClick'],
		accessRequests: TitleAndButtonsProps['accessRequests'];

	if (isShareToSlackDialogOpen) {
		title = 'slack';
		onBackClick = closeShareToSlackDialog;
	} else if (isPublicLinkDialogOpen) {
		title = 'publicLink';
		onBackClick = closePublicLinkDialog;
	} else if (isAccessRequestsDialogOpen) {
		title = 'accessRequests';
		onBackClick = closeAccessRequestsDialog;
	} else if (isSpecificAccessDialogOpen) {
		title = 'specificAccess';
		onBackClick = closeSpecificAccessDialog;
	} else {
		title = 'share';
		// These are the properties for the Access Requests Button
		// Which we only want on the main Share dialog and nowhere else
		accessRequests = {
			lastTriggeredDialogVariant,
			clearElementToFocus,
			openAccessRequestsDialog,
		};
	}

	return {
		title,
		overflowMenu: makeOverflowMenuProps(statesAndActions),
		onCloseClick: dismiss,
		onBackClick,
		accessRequests,
	};
}

export function makeGeneralAccessProps(statesAndActions: StatesAndActions): GeneralAccessProps {
	const {
		derivedState: {
			isRestrictionsEditable,
			stagedGeneralAccessLevel,
			shouldShowDeletedGroupDetails,
		},
	} = statesAndActions;
	return {
		shouldShowUneditableRestrictionsInfoIcon:
			!isRestrictionsEditable &&
			UNSAFE_expValEqualsNoExposure('confluence_contextual_ask_to_edit', 'cohort', 'experiment'),
		generalAccessLevelSelectorProps: makeGeneralAccessLevelSelectorProps(statesAndActions),
		generalAccessPermissionDropdownProps:
			stagedGeneralAccessLevel === 'restricted' || shouldShowDeletedGroupDetails
				? undefined
				: makeGeneralAccessPermissionDropdownProps(statesAndActions),
	};
}

function makeOverflowMenuProps(statesAndActions: StatesAndActions): OverflowMenuProps {
	const {
		queriesState: { spaceKey, edition },
		actions: { openInspectPermsDialog },
		controllerProps: { contentId },
	} = statesAndActions;

	return {
		spaceKey,
		isSpaceAdmin: false, // TODO
		edition,
		openInspectPermsDialog,
		contentId,
	};
}

function makeGeneralAccessLevelSelectorProps(
	statesAndActions: StatesAndActions,
): GeneralAccessLevelSelectorProps {
	const {
		controllerProps: { contentType, contentSubType },
		derivedState: {
			isLockedOut,
			stagedGeneralAccessLevel,
			isRestrictionsEditable,
			willBeUnpublishedAfterApplying,
			shouldGeneralAccessMenuBeOpen,
			shouldShowDeletedGroupDetails,
			hasRestrictions,
			hasViewRestrictions,
			hasInheritedRestrictions,
		},
		queriesState: { parentPageLink, edition },
		actions: {
			changeGeneralAccessOpenRestricted,
			openSpaceAccessDetails,
			showOpenAccessWarningDialog,
		},
		masterState: { shouldResolveMismatchedRestrictions, isSaving },
	} = statesAndActions;

	return {
		isLockedOut,
		isDraft: willBeUnpublishedAfterApplying,
		hasInheritedRestrictions,
		onOpenRestrictedChange: changeGeneralAccessOpenRestricted,
		openSpaceAccessDetails,
		handleOpenAccessWarningDialog: showOpenAccessWarningDialog,
		shouldGeneralAccessMenuBeOpen,
		isEditable: isRestrictionsEditable,
		showLongSubtext: false,
		generalAccessLevel: stagedGeneralAccessLevel,
		parentPageLink,
		isLivePage: contentSubType === 'live',
		contentType,
		edition,
		hasRestrictions,
		hasViewRestrictions,
		shouldShowDeletedGroupDetails,
		shouldResolveMismatchedRestrictions,
		isSaving,
	};
}

function makeGeneralAccessPermissionDropdownProps(
	statesAndActions: StatesAndActions,
): GeneralAccessPermissionDropdownProps {
	const {
		controllerProps: { contentType, contentSubType },
		derivedState: {
			isLockedOut,
			stagedGeneralAccessLevel,
			isRestrictionsEditable,
			willBeUnpublishedAfterApplying,
			shouldShowDeletedGroupDetails,
		},
		queriesState: { hasInheritedRestrictions, edition },
		actions: { changeGeneralAccessCanViewCanEdit },
		masterState: { isSaving },
	} = statesAndActions;

	return {
		isLockedOut,
		generalAccessLevel:
			contentType === 'embed'
				? 'open-can-view'
				: (stagedGeneralAccessLevel as Exclude<GeneralAccessLevel, 'restricted'>),
		onCanViewCanEditChange: changeGeneralAccessCanViewCanEdit,
		// Drafts only have an Edit mode by nature, so dropdown is not editable
		isEditable:
			shouldShowDeletedGroupDetails ||
			(isRestrictionsEditable &&
				!willBeUnpublishedAfterApplying &&
				edition !== ConfluenceEdition.FREE),
		hasInheritedRestrictions,
		isDraft: willBeUnpublishedAfterApplying,
		isLivePage: contentSubType === 'live',
		contentType,
		isSaving,
	};
}

function makeSpecificAccessFacePileRowProps(
	statesAndActions: StatesAndActions,
): SpecificAccessFacePileRowProps {
	const {
		derivedState: { isRemoveAllButtonVisible, isRestrictionsEditable, specificAccessIds },
		masterState: { isSpecificAccessExpanded },
		actions: { removeAllSpecificAccessPrincipals },
		queriesState: { edition },
	} = statesAndActions;

	return {
		facepileRow: specificAccessIds.length > 1 ? makeFacepileRowProps(statesAndActions) : undefined,
		isExpanded: isSpecificAccessExpanded,
		onRemoveAllClick:
			isRestrictionsEditable && isRemoveAllButtonVisible
				? removeAllSpecificAccessPrincipals
				: undefined,
		edition,
	};
}

const youNameI18n = defineMessages({
	you: {
		id: 'share-and-restrict-dialog.makeBodyProps.avatarGroupData.you',
		defaultMessage: '{name} (You)',
		description: 'The name shown for the You avatar in the Facepile component',
	},
});

// Needed attributes: name, src, key
export function makeAvatarGroupData(
	statesAndActions: StatesAndActions,
): FacepileRowProps['avatarGroupData'] {
	const {
		derivedState: { principalsMap, specificAccessIds, shouldShowDeletedGroupDetails },
		queriesState: { currentUserHasAccess },
		controllerExtras: { formatMessage },
	} = statesAndActions;

	const result: FacepileRowProps['avatarGroupData'] = [];

	const addTeamAvatar = (
		usageType: ConfluenceGroupUsageType | null,
		avatarSrc: string | undefined,
	) => {
		// only one team avatar total; it prefers slot 1 (right after "You")
		const alreadyHasTeam = result.some((principal) => principal.type === 'team');
		if (alreadyHasTeam) return;

		const team = {
			key: 'teamAvatar',
			name: '',
			type: 'team' as const,
			usageType: usageType || ConfluenceGroupUsageType.TEAM_COLLABORATION,
			src: avatarSrc,
		};

		if (!result[1]) {
			// slot 1 is empty, pushing places it there if "You" is at index 0
			result.push(team);
		} else if (result[1].type === 'user' || result[1].type === 'group') {
			// displace user/group at position 1 (teams have priority)
			result.splice(1, 0, team);
		}
	};

	const addGroupAvatar = (usageType: ConfluenceGroupUsageType) => {
		// only one userbase group avatar total; it must not displace teams
		const alreadyHasGroup = result.some((principal) => principal.type === 'group');
		if (alreadyHasGroup) return;

		const group = {
			key: 'groupAvatar',
			name: '',
			type: 'group' as const,
			usageType,
		};

		if (!result[1] || shouldShowDeletedGroupDetails) {
			// take position 1 if empty, or when showing deleted group details
			result.push(group);
		} else if (result[1].type === 'user') {
			// can displace a user at position 1
			result.splice(1, 0, group);
		} else {
			// position 1 is a team — append to end
			result.push(group);
		}
	};

	const addUserAvatar = (
		principal: { id: string; name: string; avatarUrl?: string },
		isFirst: boolean,
	) => {
		const name =
			isFirst && currentUserHasAccess
				? formatMessage(youNameI18n.you, { name: principal.name })
				: principal.name;

		result.push({
			key: principal.id,
			name,
			src: principal.avatarUrl,
			type: 'user' as const,
		});
	};

	specificAccessIds.forEach((id, idx) => {
		const entry = principalsMap.get(id);
		if (!entry) return;

		const { principal } = entry;

		// When showing deleted group details, we prioritize the group visuals
		if (principal.type === 'group' || shouldShowDeletedGroupDetails) {
			if (principal.type === 'group') {
				const isTeamCollaboration =
					principal.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION;
				const isUserbaseGroup = principal.usageType === ConfluenceGroupUsageType.USERBASE_GROUP;

				if (isTeamCollaboration) {
					// All team collaboration groups should create team avatars
					const avatarSrc = 'avatarSrc' in principal ? principal.avatarSrc : undefined;
					addTeamAvatar(principal.usageType, avatarSrc);
					return;
				}

				if (isUserbaseGroup && principal.usageType) {
					addGroupAvatar(principal.usageType);
					return;
				}

				// When usageType is null (because the BE sends the data but teams are not yet supported on the FE),
				// fall back to default user base group behavior.
				if (principal.usageType === null) {
					addGroupAvatar(ConfluenceGroupUsageType.USERBASE_GROUP);
					return;
				}
			}

			// If shouldShowDeletedGroupDetails === true but this principal isn't a group,
			// we intentionally skip adding a user here.
			return;
		}

		if (principal.type === 'user') {
			addUserAvatar(
				{ id: principal.id, name: principal.name, avatarUrl: principal.avatarUrl },
				idx === 0,
			);
		}
	});

	return result;
}

function makeFacepileRowProps(statesAndActions: StatesAndActions): FacepileRowProps | undefined {
	const {
		derivedState: {
			stagedGeneralAccessLevel,
			isUnpublishedDraft,
			updatedGroupCount,
			updatedTeamCount,
		},
		masterState: { isSpecificAccessExpanded: isExpanded },
		queriesState: { currentUserHasAccess },
		controllerProps: { contentId, contentType, contentSubType, pageMode },
		actions: { toggleSpecificAccessExpanded, openSpecificAccessDialog },
	} = statesAndActions;

	return {
		avatarGroupData: makeAvatarGroupData(statesAndActions),
		groupCount: updatedGroupCount,
		teamCount: updatedTeamCount,
		isExpanded,
		onExpandClick: toggleSpecificAccessExpanded,
		onOpenSpecificAccessDialog: openSpecificAccessDialog,
		generalAccessLevel: stagedGeneralAccessLevel,
		currentUserHasAccess,
		analyticsAttributes: {
			contentId,
			contentType,
			contentSubType,
			isLivePage: contentSubType === 'live',
			isDraft: isUnpublishedDraft,
			isPublicLinkOn: false,
			pageMode,
		},
	};
}

export function makePrincipalsStagerProps(
	statesAndActions: StatesAndActions,
): PrincipalsStagerProps {
	const {
		masterState: { principalsStagerPrincipals: principals, addMode, optionalMessage, isSaving },
		derivedState: {
			isLockedOut,
			stagedGeneralAccessLevel,
			areEmailsOnlyPresent,
			isRestrictionsEditable,
			willBeUnpublishedAfterApplying,
			exceededLimit,
			specificAccessIds,
			isSharingToEmailsDisabled,
			natureOfCta,
		},
		actions: {
			changeStagedPrincipals,
			changeAddMode,
			markPrincipalsAsInvited,
			markPrincipalsAsSkipped,
			handleCtaClick,
		},
		controllerProps: { contentId, defaultSuggestedUsers },
		queriesState: { currentUser },
	} = statesAndActions;
	const noStagedPrincipals = principals.length === 0;

	function getDropdown() {
		if (stagedGeneralAccessLevel === 'open-can-edit') return 'hidden';
		if (noStagedPrincipals) return 'hidden';
		if (areEmailsOnlyPresent) return 'hidden';
		if (!isRestrictionsEditable) return 'hidden';

		return addMode === 'as-editor' ? 'can-edit' : 'can-view';
	}

	const currentUserEmailDomain =
		currentUser?.type === 'user' ? currentUser.email?.split('@')[1] : undefined;

	const charLimitExceeded = optionalMessage.length > 500;

	return {
		isLockedOut,
		principals,
		exceededLimit,
		dropdown: getDropdown(),
		isDropdownEditable: !willBeUnpublishedAfterApplying,
		onChangePrincipals: changeStagedPrincipals,
		onChangeDropdown: changeAddMode,
		specificAccessIds,
		isSharingToEmailsDisabled,
		contentId,
		defaultSuggestedUsers,
		currentUserEmailDomain,
		markPrincipalsAsInvited,
		markPrincipalsAsSkipped,
		handleCtaClick,
		isCtaInProgress: isSaving,
		charLimitExceeded,
		natureOfCta,
	};
}

function makeSpecificAccessFilterProps(
	statesAndActions: StatesAndActions,
): SpecificAccessFilterProps {
	const {
		masterState: { specificAccessFilterText },
		actions: { setSpecificAccessFilterText },
		derivedState: { updatedGroupCount, updatedTeamCount, userCount },
	} = statesAndActions;

	return {
		setFilterText: setSpecificAccessFilterText,
		filterText: specificAccessFilterText,
		countOfGroups: updatedGroupCount,
		countOfIndividuals: userCount,
		countOfTeams: updatedTeamCount,
	};
}
